import { Link, Outlet } from "react-router-dom";
import { RoutePending } from "@/lib/shared-ui/a11y/RoutePending";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PageLayout } from "@/components/layout/PageLayout";
import { PageHeader } from "@/components/shared/PageHeader";
import { ReportsLayout } from "@/components/reports/ReportsLayout";
import { useAccessContext } from "@/hooks/useAccessContext";
import { useOrganization } from "@/hooks/useOrganization";
import { useUiUplift, useUiUpliftSettled } from "@/hooks/useUiUplift";
import { hasOrgWideReportAccess } from "@/lib/reports/report-access";
import { ReportRecordForbidden } from "./record/ReportRecordForbidden";

// RPT-1 — route-level gate for the org-wide report surfaces (/reports, the ReportRunner and
// print views, packs, board summary). Gating here, BEFORE the page components mount, means a
// scope-restricted non-admin never even fires the org-wide data hooks (usePeople,
// directory_profiles, per-report queries) — a direct fetch of org-wide rows via a saved
// Run/Print URL is prevented, not just hidden. Admins/owners and unrestricted staff pass through.
//
// `recordRoute`: the gate in front of a report record (`/reports/:reportKey`, and its sections).
// Under ui_uplift next a denied viewer gets the record's T2 no-permission state (the report's
// name, locked tabs) instead of the library card; classic is unchanged.
export function ReportsAccessGate({ recordRoute = false }: { recordRoute?: boolean } = {}) {
  const { organizationId, isOrgAdmin } = useOrganization();
  const { data: accessContext, isLoading } = useAccessContext();
  const nextLayout = useUiUplift() === "next";
  const uiSettled = useUiUpliftSettled();

  // Wait for access context so we don't briefly mount org-wide hooks before deciding.
  if (isLoading) return <RoutePending><ReportsAccessLoadingState /></RoutePending>;

  // Count only scopes for the ACTIVE org — useAccessContext fetches role_scopes by user_id
  // across orgs, so an unrestricted-here user scoped in another org must not be blocked.
  const scopeCount = (accessContext?.scopedEntities ?? []).filter(
    (scope) => scope.organization_id === organizationId,
  ).length;

  const orgWide = hasOrgWideReportAccess({
    isSuperAdmin: accessContext?.capabilities.isSuperAdmin ?? false,
    isOrgAdmin,
    scopeCount,
    // Fail closed: if the role_scopes lookup didn't reliably resolve, don't treat
    // scopeCount === 0 as "unrestricted" for a non-admin.
    scopesReliable: accessContext?.scopedEntitiesReliable ?? false,
  });

  if (orgWide) return <Outlet />;

  const leadsMinistry = (accessContext?.leader_ministries.length ?? 0) > 0;

  if (recordRoute) {
    // Neither layout is chosen until the variant settles, as the runner itself waits.
    if (!uiSettled) return <RoutePending><ReportsAccessLoadingState /></RoutePending>;
    if (nextLayout) return <ReportRecordForbidden leadsMinistry={leadsMinistry} />;
  }

  return (
    <ReportsLayout>
      <PageLayout title="Reports" hideTitle>
        <div className="container space-y-[var(--density-page-gap)] pb-24 pt-6 md:pb-6">
          <div className="hero-zone">
            <PageHeader
              title="Reports"
              subtitle="Run, schedule, and explore reports across every module."
            />
          </div>
          <Card>
            <CardHeader>
              <CardTitle>Org-wide reports are limited to admins</CardTitle>
              <CardDescription>
                Your access is scoped, so the full reporting library isn&apos;t available here.
                {leadsMinistry
                  ? " You can self-serve your ministry's participation, serving, and roster from your leader reports."
                  : " Reports scoped to your area aren't available yet — ask an admin if you need a specific report."}
              </CardDescription>
            </CardHeader>
            {leadsMinistry && (
              <CardContent>
                <Button asChild>
                  <Link to="/volunteer/leader/reports">Go to my leader reports</Link>
                </Button>
              </CardContent>
            )}
          </Card>
        </div>
      </PageLayout>
    </ReportsLayout>
  );
}

function ReportsAccessLoadingState() {
  return (
    <ReportsLayout>
      <PageLayout title="Reports" hideTitle>
        <div className="container space-y-[var(--density-page-gap)] pb-24 pt-6 md:pb-6">
          <div className="hero-zone">
            <PageHeader
              title="Reports"
              subtitle="Run, schedule, and explore reports across every module."
            />
          </div>
          <Card
            role="status"
            aria-busy="true"
            aria-live="polite"
            aria-label="Checking report access"
          >
            <CardHeader>
              <CardTitle>Checking report access</CardTitle>
              <CardDescription>
                Loading the permissions needed for the reporting library.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Skeleton className="h-4 w-full max-w-xl" />
              <Skeleton className="h-4 w-5/6 max-w-lg" />
              <Skeleton className="h-10 w-40" />
            </CardContent>
          </Card>
        </div>
      </PageLayout>
    </ReportsLayout>
  );
}
